/**
 * Reporting API
 * 
 * Frontend API utilities for managing evaluation reports and exports.
 * Handles report viewing, PDF export, rubric details, and report management.
 */

import { callFirebaseFunction } from '../firebaseCalls';

/**
 * Get the generated report for a presentation
 * @param {string} presentationId - ID of the presentation
 * @returns {Promise<any>} Report data with markdown content and evaluation results
 */
export const getPresentationReport = async (presentationId: string | undefined): Promise<any> => {
  try {
    if (!presentationId) throw new Error('Presentation ID is required');

    const result = await callFirebaseFunction('getPresentationReport', {
      presentationId
    }) as { success: boolean; report?: any; presentation?: any; error?: string };

    return result;
  } catch (error) {
    console.error('Error getting presentation report:', error);
    throw error;
  }
};

/**
 * Get detailed rubric view with criterion-level feedback
 * @param {string} presentationId - ID of the presentation
 * @param {string} category - Optional category filter
 * @returns {Promise<Object>} Detailed rubric data
 */
export const getRubricDetailView = async (presentationId: string, category: string | null = null) => {
  try {
    const result = await callFirebaseFunction('getRubricDetailView', {
      presentationId,
      category
    }) as { success: boolean; rubric?: any; error?: string };

    return result;
  } catch (error) {
    console.error('Error getting rubric detail view:', error);
    throw error;
  }
};

/**
 * Get speaker analysis results for a presentation
 * @param {string} presentationId - ID of the presentation
 * @returns {Promise<Object>} Speaker analysis data with roles, confidence, reasoning, and labeled segments
 */
export const getSpeakerAnalysis = async (presentationId: string) => {
  try {
    const result = await callFirebaseFunction('getSpeakerAnalysis', {
      presentationId
    }) as { success: boolean; speakerAnalysis?: any; summary?: any; transcriptInfo?: any; error?: string };

    return result;
  } catch (error) {
    console.error('Error getting speaker analysis:', error);
    throw error;
  }
};

/**
 * Export report as PDF
 * @param {string} presentationId - ID of the presentation
 * @returns {Promise<Object>} PDF export result with download URL
 */
export const exportReportAsPDF = async (presentationId: string) => {
  try {
    // Backend function is named `exportReportAsPdf` (note casing) in the TS Functions codebase.
    // Keep the frontend API name stable but call the deployed callable name.
    const result = await callFirebaseFunction('exportReportAsPdf', {
      presentationId
    }) as { success: boolean; downloadUrl?: string; pdfBase64?: string; fileName?: string; error?: string;[key: string]: any };

    // If the PDF was generated successfully and we have a download URL, open in new window
    if (result.success && result.downloadUrl) {
      // Open PDF in new window/tab
      window.open(result.downloadUrl, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');

      // Return success with additional info
      return {
        ...result,
        openedInNewWindow: true
      };
    }

    // If backend returns base64 PDF (current TS implementation), open it via Blob URL.
    if (result.success && result.pdfBase64) {
      try {
        const binary = atob(result.pdfBase64);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        const blob = new Blob([bytes], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);

        window.open(url, '_blank', 'width=800,height=600,scrollbars=yes,resizable=yes');

        return {
          ...result,
          downloadUrl: url,
          openedInNewWindow: true
        };
      } catch (e) {
        console.error('Error opening base64 PDF:', e);
      }
    }

    return result;
  } catch (error) {
    console.error('Error exporting report as PDF:', error);
    throw error;
  }
};

/**
 * Get markdown content for copying to clipboard
 * @param {string} presentationId - ID of the presentation
 * @returns {Promise<any>} Clean markdown content
 */
export const copyReportMarkdown = async (presentationId: string | undefined): Promise<any> => {
  try {
    if (!presentationId) throw new Error('Presentation ID is required');
    const result = await callFirebaseFunction('copyReportMarkdown', {
      presentationId
    }) as { success: boolean; markdownContent?: string; wordCount?: number; error?: string };

    return result;
  } catch (error) {
    console.error('Error getting markdown for copy:', error);
    throw error;
  }
};

/**
 * Get list of available reports for the tenant
 * @param {Object} options - Query options
 * @param {number} options.limit - Maximum number of reports to return
 * @param {string} options.startAfter - Pagination cursor
 * @returns {Promise<Object>} List of reports with presentation details
 */
export const getPresentationReportsList = async (options: Record<string, any> = {}) => {
  try {
    const result = await callFirebaseFunction('getPresentationReportsList', {
      limit: options.limit || 50,
      startAfter: options.startAfter
    });

    return result;
  } catch (error) {
    console.error('Error getting reports list:', error);
    throw error;
  }
};

/**
 * Regenerate report for a presentation (Admin only)
 * @param {string} presentationId - ID of the presentation
 * @returns {Promise<Object>} Regeneration result
 */
export const regeneratePresentationReport = async (presentationId: string) => {
  try {
    const result = await callFirebaseFunction('regeneratePresentationReport', {
      presentationId
    }) as { success: boolean; report?: any; error?: string };

    return result;
  } catch (error) {
    console.error('Error regenerating presentation report:', error);
    throw error;
  }
};

/**
 * Helper function to copy text to clipboard
 * @param {string} text - Text to copy
 * @returns {Promise<boolean>} Success status
 */
export const copyToClipboard = async (text: string) => {
  try {
    if (navigator.clipboard && window.isSecureContext) {
      // Use modern Clipboard API
      await navigator.clipboard.writeText(text);
      return true;
    } else {
      // Fallback for older browsers
      const textArea = document.createElement('textarea');
      textArea.value = text;
      textArea.style.position = 'fixed';
      textArea.style.left = '-999999px';
      textArea.style.top = '-999999px';
      document.body.appendChild(textArea);
      textArea.focus();
      textArea.select();

      const successful = document.execCommand('copy');
      document.body.removeChild(textArea);

      return successful;
    }
  } catch (error) {
    console.error('Error copying to clipboard:', error);
    return false;
  }
};

/**
 * Download text content as a file
 * @param {string} content - File content
 * @param {string} filename - Name of the file
 * @param {string} mimeType - MIME type of the file
 */
export const downloadTextFile = (content: string, filename: string, mimeType: string = 'text/plain') => {
  try {
    const blob = new Blob([content], { type: mimeType });
    const url = URL.createObjectURL(blob);

    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.style.display = 'none';

    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    // Clean up the URL object
    URL.revokeObjectURL(url);
  } catch (error) {
    console.error('Error downloading file:', error);
    throw error;
  }
};

/**
 * Get report statistics for dashboard
 * @returns {Promise<Object>} Report statistics
 */
export const getReportStatistics = async () => {
  try {
    // This would typically be a separate backend function
    // For now, we'll derive it from the reports list
    const result = await getPresentationReportsList({ limit: 1000 }) as { success: boolean; reports?: any[]; error?: string };

    if (!result.success) {
      throw new Error(result.error || 'Failed to get reports');
    }

    const reports = result.reports || [];
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getTime() - (30 * 24 * 60 * 60 * 1000));
    const sevenDaysAgo = new Date(now.getTime() - (7 * 24 * 60 * 60 * 1000));

    const recentReports = reports.filter((report: any) => {
      const generatedAt = report.generatedAt?.toDate?.() || new Date(report.generatedAt);
      return generatedAt >= thirtyDaysAgo;
    });

    const weeklyReports = reports.filter(report => {
      const generatedAt = report.generatedAt?.toDate?.() || new Date(report.generatedAt);
      return generatedAt >= sevenDaysAgo;
    });

    return {
      success: true,
      statistics: {
        totalReports: reports.length,
        reportsThisMonth: recentReports.length,
        reportsThisWeek: weeklyReports.length,
        averageReportsPerWeek: Math.round(reports.length / 52), // Rough estimate
        mostActiveConsultants: getMostActiveConsultants(reports),
        recentActivity: getRecentActivity(reports.slice(0, 10))
      }
    };
  } catch (error) {
    console.error('Error getting report statistics:', error);
    return {
      success: false,
      error: (error as any).message
    };
  }
};

/**
 * Helper function to get most active consultants from reports
 * @param {Array} reports - Array of report objects
 * @returns {Array} Top consultants by report count
 */
const getMostActiveConsultants = (reports: any[]) => {
  const consultantCounts: Record<string, number> = {};

  reports.forEach((report: any) => {
    const consultant = report.consultant;
    if (consultant && consultant.fullName) {
      const key = consultant.fullName;
      consultantCounts[key] = (consultantCounts[key] || 0) + 1;
    }
  });

  return Object.entries(consultantCounts)
    .sort(([, a]: [string, number], [, b]: [string, number]) => b - a)
    .slice(0, 5)
    .map(([name, count]) => ({ name, count }));
};

/**
 * Helper function to get recent activity from reports
 * @param {Array} reports - Array of recent report objects
 * @returns {Array} Recent activity items
 */
const getRecentActivity = (reports: any[]) => {
  return reports.map((report: any) => ({
    id: report.reportId,
    type: 'report_generated',
    description: `Report generated for ${report.presentation?.description || 'presentation'}`,
    consultant: report.consultant?.fullName || 'Unknown',
    timestamp: report.generatedAt,
    presentationId: report.presentationId
  }));
};

/**
 * Format score for display
 * @param {number} score - Raw score value
 * @param {number} maxScore - Maximum possible score (default: 5)
 * @returns {string} Formatted score string
 */
export const formatScore = (score: number | null | undefined, maxScore: number = 5) => {
  if (typeof score !== 'number') return 'N/A';
  return `${score.toFixed(1)}/${maxScore}`;
};

/**
 * Get score color for UI display
 * @param {number} score - Score value
 * @param {number} maxScore - Maximum possible score (default: 5)
 * @returns {string} Color name for Material-UI
 */
export const getScoreColor = (score: number | null | undefined, maxScore: number = 5) => {
  if (typeof score !== 'number') return 'default';

  const percentage = (score / maxScore) * 100;

  if (percentage >= 80) return 'success';
  if (percentage >= 60) return 'warning';
  if (percentage >= 40) return 'info';
  return 'error';
};

/**
 * Get score grade letter
 * @param {number} score - Score value
 * @param {number} maxScore - Maximum possible score (default: 5)
 * @returns {string} Grade letter (A, B, C, D, F)
 */
export const getScoreGrade = (score: number | null | undefined, maxScore: number = 5): string => {
  if (typeof score !== 'number') return 'N/A';

  const percentage = (score / maxScore) * 100;

  if (percentage >= 90) return 'A';
  if (percentage >= 80) return 'B';
  if (percentage >= 70) return 'C';
  if (percentage >= 60) return 'D';
  return 'F';
};